import React, { useState, useEffect, useCallback } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList } from 'recharts';
import '../styles/PendingFees.css';

const PendingFees = () => {
    const [pendingFees, setPendingFees] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Filter states
    const [searchTerm, setSearchTerm] = useState('');
    const [searchField, setSearchField] = useState('studentId');
    const [branchFilter, setBranchFilter] = useState('all');
    const [locationFilter, setLocationFilter] = useState('All');
    const [courseTypeFilter, setCourseTypeFilter] = useState('All');
    const [dateFilterType, setDateFilterType] = useState('all');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [recordsPerPage] = useState(10);

    // Sorting states
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

    // Options for filters - separate state to maintain all options
    const [allBranches, setAllBranches] = useState(['all']);
    const [allLocations, setAllLocations] = useState(['All']);
    const [allCourseTypes, setAllCourseTypes] = useState(['All']);

    // New state for location-branch mapping
    const [locationBranchMap, setLocationBranchMap] = useState({});
    const [filteredBranches, setFilteredBranches] = useState(['all']);

    // Stats
    const [stats, setStats] = useState({
        totalPendingAmount: 0,
        totalRecords: 0,
        branchWiseStats: {}
    });

    // Chart data
    const [chartData, setChartData] = useState([]);

    const searchFieldOptions = [
        { value: 'studentId', label: 'Student ID' },
        { value: 'name', label: 'Name' },
        { value: 'contactNo', label: 'Contact No' },
        { value: 'courseType', label: 'Course Type' },
        { value: 'course', label: 'Course' },
        { value: 'adminEmpName', label: 'Admin Employee' },
        { value: 'adminlocation', label: 'Location' },
        { value: 'courseFees', label: 'Course Fees' },
        { value: 'totalPendingFees', label: 'Total Pending Fees' },
        { value: 'dateOfAdmission', label: 'Admission Date' },
        { value: 'latestPendingFeesDate', label: 'Latest Pending Fees Date' }
    ];

    // Function to calculate total pending fees
    const calculateTotalPendingFees = (record) => {
        const fees1 = parseFloat(record.pendingFees) || 0;
        const fees2 = parseFloat(record.pendingFees2) || 0;
        const fees3 = parseFloat(record.pendingFees3) || 0;
        const fees4 = parseFloat(record.pendingFees4) || 0;
        return fees1 + fees2 + fees3 + fees4;
    };

    // Function to get the latest pending fees date
    const getLatestPendingFeesDate = (record) => {
        const dates = [
            record.pendingFeesDate,
            record.pendingFeesDate2,
            record.pendingFeesDate3,
            record.pendingFeesDate4
        ].filter(date => date && date !== null && date !== ''); // Filter out null/empty dates

        if (dates.length === 0) return null;

        // Convert to Date objects and find the latest
        const validDates = dates.map(dateStr => new Date(dateStr)).filter(date => !isNaN(date));

        if (validDates.length === 0) return null;

        return new Date(Math.max(...validDates));
    };

    const fetchPendingFees = useCallback(async () => {
        setLoading(true);
        setError('');

        try {
            const params = new URLSearchParams();

            if (searchTerm && searchField) {
                params.append('search', searchTerm);
                params.append('searchField', searchField);
            }

            if (branchFilter !== 'all') {
                params.append('branchFilter', branchFilter);
            }

            if (locationFilter !== 'All') {
                params.append('location', locationFilter);
            }

            if (courseTypeFilter !== 'All') {
                params.append('courseType', courseTypeFilter);
            }

            // Updated date filter logic
            if (dateFilterType === 'range' && startDate && endDate) {
                params.append('fromDate', startDate);
                params.append('toDate', endDate);
            } else if (dateFilterType === 'today') {
                // Send dueToday parameter instead of date range for today's pending fees
                params.append('dueToday', 'true');
            }

            const token = localStorage.getItem('token');
            const response = await fetch(`${process.env.REACT_APP_API_URL}/pending-details?${params}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error('Failed to fetch pending fees data');
            }

            const data = await response.json();

            // Process the data to add calculated fields
            const processedData = (data.pendingDetails || []).map(record => ({
                ...record,
                totalPendingFees: calculateTotalPendingFees(record),
                latestPendingFeesDate: getLatestPendingFeesDate(record)
            }));

            setPendingFees(processedData);

            // Store original data only on first load (when no filters are applied)
            if (searchTerm === '' && branchFilter === 'all' && locationFilter === 'All' &&
                courseTypeFilter === 'All' && dateFilterType === 'all') {
                // Extract unique values for filters from original data
                extractFilterOptions(processedData);
            }

            // Calculate stats with processed data
            calculateStats(processedData);

            // Prepare chart data
            prepareChartData(processedData);

        } catch (err) {
            console.error('Error fetching pending fees:', err);
            setError('Failed to load pending fees data. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [searchTerm, searchField, branchFilter, locationFilter, courseTypeFilter, dateFilterType, startDate, endDate]);

    useEffect(() => {
        fetchPendingFees();
    }, [fetchPendingFees]);

    const calculateStats = (data) => {
        const totalPendingAmount = data.reduce((sum, record) => sum + (record.totalPendingFees || 0), 0);
        const branchWiseStats = {};

        data.forEach(record => {
            const branch = getBranchFromStudentId(record.studentId);
            if (!branchWiseStats[branch]) {
                branchWiseStats[branch] = { count: 0, amount: 0 };
            }
            branchWiseStats[branch].count++;
            branchWiseStats[branch].amount += record.totalPendingFees || 0;
        });

        setStats({
            totalPendingAmount,
            totalRecords: data.length,
            branchWiseStats
        });
    };

    const prepareChartData = (data) => {
        const branchStats = {};

        data.forEach(record => {
            const branch = getBranchFromStudentId(record.studentId);
            if (!branchStats[branch]) {
                branchStats[branch] = {
                    branch: branch,
                    pendingAmount: 0,
                    studentCount: 0
                };
            }
            branchStats[branch].pendingAmount += record.totalPendingFees || 0;
            branchStats[branch].studentCount++;
        });

        // Convert to array and sort by pending amount (descending)
        const chartDataArray = Object.values(branchStats)
            .filter(item => item.branch !== 'Unknown') // Filter out unknown branches
            .sort((a, b) => b.pendingAmount - a.pendingAmount);

        setChartData(chartDataArray);
    };

    const extractFilterOptions = (data) => {
        const branches = new Set(['all']);
        const locations = new Set(['All']);
        const courseTypes = new Set(['All']);
        const locationBranchMapping = {};

        data.forEach(record => {
            const branch = getBranchFromStudentId(record.studentId);
            const location = record.adminlocation;

            if (branch !== 'Unknown') { // Only add valid branches
                branches.add(branch);
            }

            if (location) {
                locations.add(location);

                // Create location-branch mapping
                if (!locationBranchMapping[location]) {
                    locationBranchMapping[location] = new Set();
                }
                if (branch !== 'Unknown') {
                    locationBranchMapping[location].add(branch);
                }
            }

            if (record.courseType) courseTypes.add(record.courseType);
        });

        // Convert sets to arrays for location-branch mapping
        const finalLocationBranchMap = {};
        Object.keys(locationBranchMapping).forEach(location => {
            finalLocationBranchMap[location] = ['all', ...Array.from(locationBranchMapping[location])];
        });

        setAllBranches([...branches]);
        setAllLocations([...locations]);
        setAllCourseTypes([...courseTypes]);
        setLocationBranchMap(finalLocationBranchMap);
        setFilteredBranches([...branches]); // Initially show all branches
    };

    // Function to reset filters and reload all data
    const resetFilters = async () => {
        setSearchTerm('');
        setSearchField('studentId');
        setBranchFilter('all');
        setLocationFilter('All');
        setCourseTypeFilter('All');
        setDateFilterType('all');
        setStartDate('');
        setEndDate('');
        setCurrentPage(1);

        // Reset filtered branches to show all
        setFilteredBranches(allBranches);

        // Fetch all data without any filters
        await fetchAllData();
    };

    // Function to fetch all data without filters
    const fetchAllData = async () => {
        setLoading(true);
        setError('');

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${process.env.REACT_APP_API_URL}/pending-details`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error('Failed to fetch pending fees data');
            }

            const data = await response.json();

            // Process the data to add calculated fields
            const processedData = (data.pendingDetails || []).map(record => ({
                ...record,
                totalPendingFees: calculateTotalPendingFees(record),
                latestPendingFeesDate: getLatestPendingFeesDate(record)
            }));

            setPendingFees(processedData);

            // Extract filter options from all data
            extractFilterOptions(processedData);

            // Calculate stats with processed data
            calculateStats(processedData);

            // Prepare chart data
            prepareChartData(processedData);

        } catch (err) {
            console.error('Error fetching pending fees:', err);
            setError('Failed to load pending fees data. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const getBranchFromStudentId = (studentId) => {
        if (!studentId) return 'Unknown';

        const branchCodeMap = {
            'TM': 'Tambaram',
            'VL': 'Velachery',
            'VP': 'Vadapalani',
            'PM': 'Poonamallee',
            'MH': 'Marathahalli',
            'GP': 'Gandhipuram',
            'MP': 'Malumichampatti',
            'HS': 'Hosur',
            'SP': 'Saravanampatti',
            'TP': 'Tiruppur',
            'PD': 'Padi'
        };

        const match = studentId.match(/-([A-Z]{2})-/);
        return match && branchCodeMap[match[1]] ? branchCodeMap[match[1]] : 'Unknown';
    };

    const formatCurrency = (amount) => {
        const numAmount = parseFloat(amount) || 0;
        return `₹${numAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
    };

    const formatDate = (dateInput) => {
        if (!dateInput) return 'N/A';

        let date;
        if (dateInput instanceof Date) {
            date = dateInput;
        } else {
            date = new Date(dateInput);
        }

        if (isNaN(date)) return 'N/A';

        return date.toLocaleDateString('en-IN');
    };

    // Custom tooltip for the chart
    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload;
            return (
                <div className="chart-tooltip">
                    <p className="tooltip-label">{`Branch: ${label}`}</p>
                    <p className="tooltip-amount">
                        {`Pending Amount: ${formatCurrency(data.pendingAmount)}`}
                    </p>
                    <p className="tooltip-count">
                        {`Students: ${data.studentCount}`}
                    </p>
                </div>
            );
        }
        return null;
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    const handleSearchFieldChange = (e) => {
        setSearchField(e.target.value);
        setSearchTerm(''); // Clear search term when field changes
    };

    const handleBranchChange = (e) => {
        setBranchFilter(e.target.value);
    };

    const handleLocationChange = (e) => {
        const selectedLocation = e.target.value;
        setLocationFilter(selectedLocation);

        // Update filtered branches based on selected location
        if (selectedLocation === 'All') {
            setFilteredBranches(allBranches);
        } else {
            const branchesForLocation = locationBranchMap[selectedLocation] || ['all'];
            setFilteredBranches(branchesForLocation);
        }

        // Reset branch filter to 'all' when location changes
        setBranchFilter('all');
    };

    const handleCourseTypeChange = (e) => {
        setCourseTypeFilter(e.target.value);
    };

    const handleDateFilterChange = (type) => {
        setDateFilterType(type);
        if (type !== 'range') {
            setStartDate('');
            setEndDate('');
        }
    };

    const applyFilters = () => {
        setCurrentPage(1); // Reset to first page
        fetchPendingFees();
    };

    const requestSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getSortIndicator = (columnName) => {
        if (sortConfig.key === columnName) {
            return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
        }
        return '';
    };

    // Check if branch filter should be disabled
    const isBranchFilterDisabled = locationFilter === 'All';

    // Sort and paginate data
    const sortedData = React.useMemo(() => {
        let sortableData = [...pendingFees];
        if (sortConfig.key) {
            sortableData.sort((a, b) => {
                let aValue = a[sortConfig.key];
                let bValue = b[sortConfig.key];

                // Handle numeric fields
                if (['courseFees', 'discountAmount', 'feesCollected', 'totalPendingFees'].includes(sortConfig.key)) {
                    aValue = parseFloat(aValue) || 0;
                    bValue = parseFloat(bValue) || 0;
                }

                // Handle date fields
                if (['dateOfAdmission', 'latestPendingFeesDate'].includes(sortConfig.key)) {
                    aValue = new Date(aValue || 0);
                    bValue = new Date(bValue || 0);
                }

                if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
                if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }
        return sortableData;
    }, [pendingFees, sortConfig]);

    // Pagination
    const indexOfLastRecord = currentPage * recordsPerPage;
    const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
    const currentRecords = sortedData.slice(indexOfFirstRecord, indexOfLastRecord);
    const totalPages = Math.ceil(sortedData.length / recordsPerPage);

    const paginate = (pageNumber) => setCurrentPage(pageNumber);
    const goToPreviousPage = () => setCurrentPage(prev => Math.max(prev - 1, 1));
    const goToNextPage = () => setCurrentPage(prev => Math.min(prev + 1, totalPages));

    return (
        <div className="pending-fees-container">
            <h1>Pending Fees Dashboard</h1>

            {error && <div className="error-message">{error}</div>}

            <div className="dashboard-controls">
                <div className="search-filter">
                    <select
                        value={searchField}
                        onChange={handleSearchFieldChange}
                        className="search-field-selector"
                    >
                        {searchFieldOptions.map(option => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>

                    <input
                        type="text"
                        placeholder={`Search by ${searchFieldOptions.find(opt => opt.value === searchField)?.label}...`}
                        value={searchTerm}
                        onChange={handleSearchChange}
                        className="searchpf-input"
                    />

                    <select
                        value={locationFilter}
                        onChange={handleLocationChange}
                        className="location-filter"
                    >
                        {allLocations.map(location => (
                            <option key={location} value={location}>
                                {location === 'All' ? 'All Locations' : location}
                            </option>
                        ))}
                    </select>

                    <select
                        value={branchFilter}
                        onChange={handleBranchChange}
                        className="branch-filter"
                        disabled={isBranchFilterDisabled}
                        style={{
                            opacity: isBranchFilterDisabled ? 0.5 : 1,
                            cursor: isBranchFilterDisabled ? 'not-allowed' : 'pointer'
                        }}
                    >
                        {isBranchFilterDisabled ? (
                            <option value="all">Select Location First</option>
                        ) : (
                            filteredBranches.map(branch => (
                                <option key={branch} value={branch}>
                                    {branch === 'all' ? 'All Branches' : branch}
                                </option>
                            ))
                        )}
                    </select>

                    <select
                        value={courseTypeFilter}
                        onChange={handleCourseTypeChange}
                        className="course-type-filter"
                    >
                        {allCourseTypes.map(courseType => (
                            <option key={courseType} value={courseType}>
                                {courseType === 'All' ? 'All Course Types' : courseType}
                            </option>
                        ))}
                    </select>

                    <button onClick={applyFilters} className="refresh-btn">
                        Apply Filters
                    </button>

                    <button onClick={resetFilters} className="reset-btn">
                        Reset Filters
                    </button>
                    <button
                        className="cancel3-btn"
                        onClick={() => window.location.href = '/dashboard'}
                    >
                        Back
                    </button>
                </div>

                {/* Date filter controls */}
                <div className="date-filter">
                    <div className="date-filter-options">
                        <label className={`date-option ${dateFilterType === 'all' ? 'active' : ''}`}>
                            <input
                                type="radio"
                                name="dateFilter"
                                checked={dateFilterType === 'all'}
                                onChange={() => handleDateFilterChange('all')}
                            />
                            All Dates
                        </label>
                        <label className={`date-option ${dateFilterType === 'today' ? 'active' : ''}`}>
                            <input
                                type="radio"
                                name="dateFilter"
                                checked={dateFilterType === 'today'}
                                onChange={() => handleDateFilterChange('today')}
                            />
                            Today's Pending fees
                        </label>
                        <label className={`date-option ${dateFilterType === 'range' ? 'active' : ''}`}>
                            <input
                                type="radio"
                                name="dateFilter"
                                checked={dateFilterType === 'range'}
                                onChange={() => handleDateFilterChange('range')}
                            />
                            Date Range
                        </label>
                    </div>

                    {dateFilterType === 'range' && (
                        <div className="date-range-inputs">
                            <div className="date-input-group">
                                <label>From:</label>
                                <input
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    className="date-input"
                                />
                            </div>
                            <div className="date-input-group">
                                <label>To:</label>
                                <input
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    className="date-input"
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div className="dashboard-stats">
                <div className="stat1-card">
                    <h3>Total Pending Amount</h3>
                    <p>{formatCurrency(stats.totalPendingAmount)}</p>
                </div>

                <div className="stat-card">
                    <h3>Students with Outstanding fees</h3>
                    <p>{stats.totalRecords}</p>
                </div>
            </div>

            {/* Branch-wise Bar Chart */}
            {chartData.length > 0 && (
                <div className="chart-container">
                    <h2>Branch-wise Pending Fees Analysis</h2>
                    <div className="chart-wrapper">
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart
                                data={chartData}
                                layout="vertical"
                                margin={{ top: 20, right: 50, left: 60, bottom: 20 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                                <XAxis
                                    type="number"
                                    tickFormatter={(value) => {
                                        const max = Math.max(...chartData.map(item => item.pendingAmount));
                                        return `${Math.round((value / max) * 100)}%`;
                                    }}
                                    stroke="#666"
                                    fontSize={11}
                                />
                                <YAxis
                                    type="category"
                                    dataKey="branch"
                                    width={90}
                                    tickLine={false}
                                    axisLine={false}
                                    stroke="#666"
                                    fontSize={11}
                                />
                                <Tooltip content={<CustomTooltip />} />
                                <Legend />
                                <Bar
                                    dataKey="pendingAmount"
                                    name="Pending Amount"
                                    fill="#efb102"
                                    barSize={30}
                                >
                                    <LabelList
                                        dataKey="studentCount"
                                        position="right"
                                        formatter={(value) => `${value} students`}
                                        style={{ fill: '#333', fontSize: 12 }}
                                    />
                                </Bar>

                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            )}

            {loading ? (
                <div className="loading-spinner">Loading...</div>
            ) : (
                <>
                    {currentRecords.length > 0 ? (
                        <>
                            <div className="table-container">
                                <table className="pending-fees-table">
                                    <thead>
                                        <tr>
                                            <th onClick={() => requestSort('studentId')}>
                                                Student ID {getSortIndicator('studentId')}
                                            </th>
                                            <th onClick={() => requestSort('name')}>
                                                Name {getSortIndicator('name')}
                                            </th>
                                            <th>Branch</th>
                                            <th onClick={() => requestSort('contactNo')}>
                                                Contact {getSortIndicator('contactNo')}
                                            </th>
                                            <th onClick={() => requestSort('courseType')}>
                                                Course Type {getSortIndicator('courseType')}
                                            </th>
                                            <th onClick={() => requestSort('course')}>
                                                Course {getSortIndicator('course')}
                                            </th>
                                            <th onClick={() => requestSort('courseFees')}>
                                                Course Fees {getSortIndicator('courseFees')}
                                            </th>
                                            <th>Discount Amount</th>
                                            <th onClick={() => requestSort('feesCollected')}>
                                                Collected {getSortIndicator('feesCollected')}
                                            </th>
                                            <th onClick={() => requestSort('totalPendingFees')}>
                                                Total Pending Amount {getSortIndicator('totalPendingFees')}
                                            </th>
                                            <th onClick={() => requestSort('latestPendingFeesDate')}>
                                                Latest Due Date {getSortIndicator('latestPendingFeesDate')}
                                            </th>
                                            <th onClick={() => requestSort('dateOfAdmission')}>
                                                Admission Date {getSortIndicator('dateOfAdmission')}
                                            </th>
                                            <th>Location</th>
                                            <th>Admin Name</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentRecords.map((record, index) => (
                                            <tr key={index}>
                                                <td>{record.studentId}</td>
                                                <td>{record.name}</td>
                                                <td>{getBranchFromStudentId(record.studentId)}</td>
                                                <td>{record.contactNo || 'N/A'}</td>
                                                <td>{record.courseType || 'N/A'}</td>
                                                <td>{record.course || 'N/A'}</td>
                                                <td className="amount-cell">{formatCurrency(record.courseFees)}</td>
                                                <td className="amount-cell">{formatCurrency(record.discountAmount)}</td>
                                                <td className="amount-cell">{formatCurrency(record.feesCollected)}</td>
                                                <td className="amount-cell pending-amount">{formatCurrency(record.totalPendingFees)}</td>
                                                <td>{formatDate(record.latestPendingFeesDate)}</td>
                                                <td>{formatDate(record.dateOfAdmission)}</td>
                                                <td>{record.adminlocation || 'N/A'}</td>
                                                <td>{record.adminEmpName || 'N/A'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination Controls */}
                            <div className="pagination-container">
                                <div className="pagination-controls">
                                    <button
                                        onClick={goToPreviousPage}
                                        disabled={currentPage === 1}
                                        className="pagination-btn"
                                    >
                                        &laquo;
                                    </button>

                                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                                        .filter(page => {
                                            return page === 1 ||
                                                page === totalPages ||
                                                (page >= currentPage - 2 && page <= currentPage + 2);
                                        })
                                        .map((page, index, filteredPages) => (
                                            <React.Fragment key={page}>
                                                {index > 0 && filteredPages[index - 1] < page - 1 && (
                                                    <span className="pagination-ellipsis">...</span>
                                                )}
                                                <button
                                                    onClick={() => paginate(page)}
                                                    className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
                                                >
                                                    {page}
                                                </button>
                                            </React.Fragment>
                                        ))
                                    }

                                    <button
                                        onClick={goToNextPage}
                                        disabled={currentPage === totalPages}
                                        className="pagination-btn"
                                    >
                                        &raquo;
                                    </button>
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="no-data-message">No pending fees records found.</div>
                    )}
                </>
            )}
        </div>
    );
};

export default PendingFees;